/**
 * Sheet — Capture flow shell. Bottom sheet on mobile, side panel on desktop.
 * Slow ease-out 320ms in, 400ms out per UX motion tokens.
 *
 * We deliberately do NOT depend on @radix-ui/react-dialog's CSS-positioning;
 * instead we reuse Radix Dialog for portal + focus trap + escape handling
 * and apply our own positioning so the bottom sheet behavior is correct.
 */
import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Visually-hidden title (still required for screen readers). */
  title: string;
  description?: string;
  children: React.ReactNode;
  /** Position of the sheet on desktop. Default 'right'. */
  side?: 'right' | 'bottom';
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  side = 'right',
}: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50',
            'bg-bg/65 backdrop-blur-md',
            'data-[state=open]:animate-[fade-in_280ms_var(--ease-out-slow)]',
            'data-[state=closed]:animate-[fade-out_320ms_var(--ease-out-slow)]',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed z-50 bg-bg border-ink-200 focus:outline-none',
            // Mobile = bottom sheet always.
            'left-0 right-0 bottom-0 max-h-[90vh] border-t',
            // Desktop = side panel or centered modal depending on side.
            side === 'right'
              ? 'md:left-auto md:right-0 md:bottom-0 md:top-0 md:max-h-none md:w-[min(560px,90vw)] md:border-t-0 md:border-l'
              : 'md:left-1/2 md:bottom-auto md:top-[8vh] md:max-h-[80vh] md:w-[min(720px,calc(100%-3rem))] md:-translate-x-1/2 md:border md:border-t',
            'overflow-y-auto',
            'data-[state=open]:animate-[sheet-in_320ms_var(--ease-out-slow)]',
            'data-[state=closed]:animate-[sheet-out_400ms_var(--ease-out-slow)]',
          )}
          aria-describedby={undefined}
        >
          <div className="sr-only">
            <Dialog.Title>{title}</Dialog.Title>
            {description ? <Dialog.Description>{description}</Dialog.Description> : null}
          </div>
          <div className="p-6 md:p-8">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
